import { useQuery } from '@tanstack/react-query'
import { PromoCodeDistributionItem, PromoCodeItem, SearchDistributionsParams } from 'Modules'

import { AppAdmin } from 'api/app-admin'
import { DISTRIBUTION_EMAIL_STATUS_TO_API, DISTRIBUTION_TYPE_TO_API } from 'constants/promo-code'
import { mapApiToDistribution } from 'modules/promo-code/api-adapters'
import { logError } from 'utils/logger'

import { promoCodeKeys } from './query-keys'

interface DistributionsResult {
  results: PromoCodeDistributionItem[]
  total: number
}

const fetchDistributions = async (
  promoCodeId: string,
  params: SearchDistributionsParams
): Promise<DistributionsResult> => {
  try {
    const res = await AppAdmin.getPromoCodeDistributions({
      promoCodeId,
      keyword: params.keyword,
      type: params.type ? DISTRIBUTION_TYPE_TO_API[params.type] : undefined,
      status: params.emailStatus ? DISTRIBUTION_EMAIL_STATUS_TO_API[params.emailStatus] : undefined,
      page: params.page,
      limit: params.limit,
      sort: 'createdAt:desc',
    })
    const payload = res.data?.data
    const results = (payload?.results ?? []).map(mapApiToDistribution)
    return { results, total: payload?.totalResults ?? results.length }
  } catch (error) {
    logError('Error fetching promo code distributions:', error)
    return { results: [], total: 0 }
  }
}

/**
 * Whitelist & Email save history — GET /admin/platform-promo-code-distributions
 * (each record is one Save & Send Email action).
 */
export const useDistributions = (
  promoCode: PromoCodeItem | null,
  params: SearchDistributionsParams
) => {
  const query = useQuery({
    queryKey: promoCodeKeys.distributions(promoCode?.id, params),
    queryFn: () => fetchDistributions(promoCode!.id, params),
    enabled: !!promoCode,
    gcTime: 30000,
  })

  return {
    distributions: query.data?.results ?? [],
    total: query.data?.total ?? 0,
    isLoading: query.isLoading,
    isFetching: query.isFetching,
  }
}
